import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/shadcn-ui/dialog';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { Textarea } from '@/components/shadcn-ui/textarea';
import { useProjectStore } from '@/stores/useProject.store';
import { appAxios } from '@/network/base/appAxios';
import { toast } from "sonner";
import { ArrowLeftIcon, PlusIcon } from "lucide-react";

function CustomerModal({ isOpen, onClose, permitChatId }) {
    const [step, setStep] = useState("customers");
    const [loading, setLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [touched, setTouched] = useState({
        owner_email: false,
        owner_phone: false,
    });
    const [customerForm, setCustomerForm] = useState({
        owner_name: "",
        owner_email: "",
        owner_phone: "",
        property_address: "",
        permit_chat_id: permitChatId,
    });

    const [projectForm, setProjectForm] = useState({
        name: "",
        description: "",
    });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;

    const isEmailValid = emailRegex.test(customerForm.owner_email);
    const isPhoneValid = phoneRegex.test(customerForm.owner_phone);

    const isCustomerFormValid =
        customerForm.owner_name.trim() !== "" &&
        customerForm.property_address.trim() !== "" &&
        isEmailValid &&
        isPhoneValid;

    const customers = useProjectStore(state => state.customers);
    const getCustomers = useProjectStore(state => state.getCustomers);
    const createCustomer = useProjectStore(state => state.createCustomer);
    const projects = useProjectStore(state => state.projects);
    const getProjectsByCustomer = useProjectStore(state => state.getProjectsByCustomer);
    const getProjects = useProjectStore(state => state.getProjects);

    useEffect(() => {
        if (isOpen) {
            getCustomers();
            setStep("customers");
            setSelectedCustomer(null);
        }
    }, [isOpen]);

    const handleSelectCustomer = async (customer) => {
        setLoading(true);
        setSelectedCustomer(customer);
        await getProjectsByCustomer(customer._id);
        setStep("projects");
        setLoading(false);
    };

    const handleCreateCustomer = async (e) => {
        e.preventDefault();
        setLoading(true);
        await createCustomer(customerForm);
        await getCustomers(true);
        setCustomerForm({
            owner_name: "",
            owner_email: "",
            owner_phone: "",
            property_address: "",
            permit_chat_id: permitChatId,
        });
        setTouched({
            owner_email: false,
            owner_phone: false,
        });
        setStep("customers");
        setLoading(false);
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        setLoading(true);

        const response = await appAxios.post('/Customer/v1.0/createCustomerProject', {
            projectName: projectForm.name,
            projectDescription: projectForm.description,
            CustomerId: selectedCustomer._id,
            permitChatId
        });
        if (response.data.status == "Success") {
            await getProjects(true);
            await getProjectsByCustomer(selectedCustomer._id);
        } else {
            toast.error(response.data.message || "Failed to create project");
        }

        setProjectForm({ name: "", description: "" });
        setStep("projects");
        setLoading(false);
    };

    const handleSelectProject = async (projectId) => {
        setLoading(true);
        try {
            await appAxios.post('/Project/v1.0/assignProjectToChat', {
                permitChatId,
                projectId,
            });

            await getProjects(true);
            onClose();
            window.location.reload();
        } catch (error) {
            console.error("Failed to assign project:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg dark text-white">
                <DialogHeader>
                    <DialogTitle className="text-xl font-baloo-2 my-1">
                        {step === "customers" && "Select a Customer"}
                        {step === "addCustomer" && "Create New Customer"}
                        {step === "projects" && "Select a Project"}
                        {step === "addProject" && "Create New Project"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        Link this chat to a project under a customer
                    </DialogDescription>
                </DialogHeader>

                {step === "customers" && (
                    <div className="space-y-3">
                        <Button
                            onClick={() => setStep("addCustomer")}
                            className="w-full flex items-center gap-2"
                            variant="outline"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add New Customer
                        </Button>

                        <div className="border-t pt-3 max-h-[300px] overflow-y-auto space-y-2">
                            {customers?.length > 0 ? (
                                customers.map((customer) => (
                                    <button
                                        key={customer._id}
                                        onClick={() => handleSelectCustomer(customer)}
                                        disabled={loading}
                                        className="w-full text-left p-4 rounded-xl border hover:bg-accent/60 transition"
                                    >
                                        <div className="font-medium">{customer.owner_name}</div>

                                        <div className="flex justify-between gap-1">
                                            {customer.owner_email ? (
                                                <div className="text-sm text-muted-foreground">
                                                    {customer.owner_email}
                                                </div>
                                            ) : (
                                                <div className="text-sm text-muted-foreground">
                                                    N/A
                                                </div>
                                            )}
                                            {customer.property_address ? (
                                                <div className="text-sm text-muted-foreground line-clamp-1">
                                                    {customer.property_address}
                                                </div>
                                            ) : (
                                                <div className="text-sm text-muted-foreground">
                                                    N/A
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))
                            ) : (
                                <div className="text-center text-sm text-muted-foreground py-4">
                                    No customers available
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {step === "addCustomer" && (
                    <form onSubmit={handleCreateCustomer} className="space-y-6">

                        <Input
                            placeholder="Owner Name"
                            value={customerForm.owner_name}
                            onChange={(e) =>
                                setCustomerForm({ ...customerForm, owner_name: e.target.value })
                            }
                            required
                        />

                        <Input
                            placeholder="Owner Email"
                            type="email"
                            value={customerForm.owner_email}
                            onBlur={() => setTouched({ ...touched, owner_email: true })}
                            onChange={(e) =>
                                setCustomerForm({ ...customerForm, owner_email: e.target.value })
                            }
                            required
                        />

                        {touched.owner_email && !isEmailValid && (
                            <p className="text-sm text-red-500">
                                {customerForm.owner_email
                                    ? "Enter a valid email address"
                                    : "Email is required"}
                            </p>
                        )}

                        <Input
                            placeholder="Owner Phone (10 digits)"
                            maxLength={10}
                            value={customerForm.owner_phone}
                            onBlur={() => setTouched({ ...touched, owner_phone: true })}
                            onChange={(e) =>
                                setCustomerForm({
                                    ...customerForm,
                                    owner_phone: e.target.value.replace(/\D/g, "")
                                })
                            }
                            required
                        />

                        {touched.owner_phone && !isPhoneValid && (
                            <p className="text-sm text-red-500">
                                {customerForm.owner_phone
                                    ? "Phone must be exactly 10 digits"
                                    : "Phone number is required"}
                            </p>
                        )}

                        <Textarea
                            placeholder="Property Address"
                            value={customerForm.property_address}
                            onChange={(e) =>
                                setCustomerForm({
                                    ...customerForm,
                                    property_address: e.target.value
                                })
                            }
                            required
                        />

                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setStep("customers");
                                    setCustomerForm({
                                        owner_name: "",
                                        owner_email: "",
                                        owner_phone: "",
                                        property_address: "",
                                        permit_chat_id: permitChatId,
                                    });
                                    setTouched({
                                        owner_email: false,
                                        owner_phone: false,
                                    });
                                }}
                            >
                                Back
                            </Button>

                            <Button
                                type="submit"
                                disabled={!isCustomerFormValid || loading}
                                className="flex-1"
                            >
                                {loading ? "Creating..." : "Create Customer"}
                            </Button>
                        </div>
                    </form>
                )}

                {step === "projects" && (
                    <div className="space-y-3">
                        <div className="flex justify-between gap-1">
                            <Button
                                variant="ghost"
                                className="w-full flex items-center gap-2"
                                onClick={() => setStep("customers")}
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
                                Change Customer
                            </Button>
                            <Button
                                onClick={() => setStep("addProject")}
                                className="w-full flex items-center gap-2"
                                variant="outline"
                            >
                                <PlusIcon className="w-4 h-4" />
                                Add New Project
                            </Button>
                        </div>
                        <div className="border-t pt-3 max-h-[300px]  space-y-2">
                            <div className="text-center">
                                <div className="font-medium">{selectedCustomer?.owner_name} Project's</div>
                            </div>
                        </div>

                        <div className="border-t pt-3 max-h-[300px] overflow-y-auto space-y-2">
                            {projects?.length > 0 ? (
                                projects.map((project) => (
                                    <button
                                        key={project._id}
                                        onClick={() => handleSelectProject(project._id)}
                                        disabled={loading}
                                        className="w-full text-left p-4 rounded-xl border hover:bg-accent/60 transition"
                                    >
                                        <div className="font-medium truncate">
                                            {project.projectName}
                                        </div>
                                        {project.projectDescription && (
                                            <div className="text-sm text-muted-foreground line-clamp-2">
                                                {project.projectDescription}
                                            </div>
                                        )}
                                    </button>
                                ))
                            ) : (
                                <div className="text-center text-sm text-muted-foreground py-4">
                                    No projects available
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {step === "addProject" && (
                    <form onSubmit={handleCreateProject} className="space-y-6">
                        <Input
                            placeholder="Project Name"
                            value={projectForm.name}
                            onChange={(e) =>
                                setProjectForm({ ...projectForm, name: e.target.value })
                            }
                            required
                        />
                        <Textarea
                            placeholder="Project Description"
                            value={projectForm.description}
                            onChange={(e) =>
                                setProjectForm({
                                    ...projectForm,
                                    description: e.target.value,
                                })
                            }
                            required
                        />
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={() => setStep("projects")}>
                                Back
                            </Button>
                            <Button type="submit" disabled={loading} className="flex-1">
                                {loading ? "Creating..." : "Create Project"}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default CustomerModal;